import React, { useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { confirmDialog } from 'primereact/confirmdialog';
import GenericFileUpload from '../../../../../components/common/GenericFileUpload';
import { InvoiceStatus } from '../constants/invoiceStatus';

const DealerInvoiceDialog = ({
    visible, onHide, formData,
    onInputChange, onDateChange, onTextareaChange,
    onSubmit, onSaveDraft, onSubmitDraft, validateForSend,
    submitted, sendAttempted,
    isSubmitting, isSavingDraft, isSending,
    isEditMode, isLoadingDetail,
    newFiles, onFilesChange, existingDocuments,
    onDeleteDocument, onDownloadDocument,
    th, isWarrantyTeam, isDomesticDealer,
    // Garanti ekibi aksiyonları dialog içinden yapılacaksa:
    onApprove, onReject, onReturn,
    isApproving, isRejecting, isReturning
}) => {
    const toast = useRef(null);

    const isDraft = formData.status === InvoiceStatus.DRAFT;
    const isEditableStatus = formData.status === InvoiceStatus.RETURNED || isDraft;

    // Dialog Başlığı
    const getDialogHeader = () => {
        if (!isEditMode) return th('dealerInvoice.dialog.createTitle');
        if (isDraft && !isWarrantyTeam) return th('dealerInvoice.dialog.editDraftTitle');
        if (formData.status === InvoiceStatus.RETURNED) return th('dealerInvoice.dialog.editReturnedTitle');
        if (isWarrantyTeam) return th('warrantyTeam.pageTitle'); // veya "Onay Ekranı"
        return th('dealerInvoice.dialog.editTitle');
    };

    // ReadOnly Kontrolü:
    // Garanti ekibi ise düzenleyemez, sadece onaylar.
    // Bayi ise, Status RETURNED değilse ve EditMode ise düzenleyemez.
    const isReadOnly = isWarrantyTeam || (isEditMode && !isEditableStatus);

    // Fatura No özel durumu: İade edilse bile Fatura No değişmemeli (genelde) ama VAF örneğinde her şey editleniyor.
    // Buradaki iş kuralına göre: Sadece RETURNED durumunda bayi düzenleyebilir.

    const saveAndSendLabel = th(isDomesticDealer ? 'dealerInvoice.dialog.saveAndSendToSap' : 'dealerInvoice.dialog.saveAndSend');
    const sendLabel = th(isDomesticDealer ? 'dealerInvoice.dialog.sendToSap' : 'dealerInvoice.dialog.sendForApproval');

    const handleSendClick = (action) => {
        if (validateForSend && !validateForSend()) return;

        confirmDialog({
            header: th('dealerInvoice.sendConfirm.title'),
            message: th(isDomesticDealer ? 'dealerInvoice.sendConfirm.messageDomestic' : 'dealerInvoice.sendConfirm.message'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('common.yes'),
            rejectLabel: th('common.no'),
            accept: action
        });
    };

    const hasNoDocuments = (!existingDocuments || existingDocuments.length === 0) && (!newFiles || newFiles.length === 0);

    // --- Footer Buton Mantığı ---
    let footerButtons;

    if (!isEditMode) {
        // YENİ KAYIT (NEW)
        footerButtons = (
            <>
                <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} className="p-button-text" disabled={isSubmitting} />
                <Button label={th('common.save')} icon="pi pi-save" onClick={onSaveDraft} loading={isSavingDraft} disabled={isSending || isLoadingDetail} />
                <Button label={saveAndSendLabel} icon="pi pi-send" onClick={() => handleSendClick(onSubmit)} loading={isSending} disabled={isSavingDraft || isLoadingDetail} className="p-button-success" />
            </>
        );
    } else {
        // DÜZENLEME / GÖRÜNTÜLEME MODU
        switch (formData.status) {
            case InvoiceStatus.DRAFT:
                if (!isWarrantyTeam) {
                    footerButtons = (
                        <>
                            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} className="p-button-text" disabled={isSubmitting} />
                            <Button label={th('common.save')} icon="pi pi-save" onClick={onSaveDraft} loading={isSavingDraft} disabled={isSending || isLoadingDetail} />
                            <Button label={sendLabel} icon="pi pi-send" onClick={() => handleSendClick(onSubmitDraft)} loading={isSending} disabled={isSavingDraft || isLoadingDetail} className="p-button-success" />
                        </>
                    );
                } else {
                    footerButtons = <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" />;
                }
                break;

            case InvoiceStatus.RETURNED: // Bayi: İade edilen kaydı düzeltip gönderecek
                if (!isWarrantyTeam) {
                    footerButtons = (
                        <>
                            <Button label={th('common.cancel')} icon="pi pi-times" onClick={onHide} className="p-button-text" disabled={isSubmitting} />
                            <Button
                                label={th('dealerInvoice.dialog.saveChanges') || 'Tekrar Gönder'}
                                icon="pi pi-send"
                                onClick={onSubmit}
                                loading={isSubmitting}
                                className="p-button-primary"
                            />
                        </>
                    );
                } else {
                    // Garanti ekibi iade ettiğini sadece görüntüler
                    footerButtons = <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" />;
                }
                break;

            case InvoiceStatus.PENDING: // Garanti Ekibi: Onaylayacak
                if (isWarrantyTeam) {
                    footerButtons = (
                        <>
                            <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" />
                            <Button label={th('warrantyTeam.return')} icon="pi pi-replay" onClick={onReturn} loading={isReturning} className="p-button-warning" />
                            <Button label={th('warrantyTeam.reject')} icon="pi pi-times-circle" onClick={onReject} loading={isRejecting} className="p-button-danger ml-2" />
                            <Button label={th('warrantyTeam.approve')} icon="pi pi-check-circle" onClick={onApprove} loading={isApproving} className="p-button-success ml-2" />
                        </>
                    );
                } else {
                    // Bayi: Onay bekliyor, işlem yapamaz
                    footerButtons = <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" />;
                }
                break;

            default: // Approved, Rejected vb.
                footerButtons = <Button label={th('common.close')} icon="pi pi-times" onClick={onHide} className="p-button-text" />;
                break;
        }
    }

    return (
        <Dialog
            visible={visible}
            style={{ width: '60vw', minWidth: '700px' }}
            header={getDialogHeader()}
            modal
            className="p-fluid"
            footer={footerButtons}
            onHide={onHide}
            breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        >
            <Toast ref={toast} />

            {isLoadingDetail ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="grid">
                    {/* Uyarı Mesajları */}
                    <div className="col-12">
                        {isEditMode && isDraft && (
                            <Message severity="info" className="w-full justify-content-start mb-3" text={th('dealerInvoice.draftInfo')} />
                        )}
                        {formData.status === InvoiceStatus.RETURNED && formData.returnReason && (
                            <Message severity="warn" className="w-full justify-content-start mb-3" text={`${th('dealerInvoice.returnedWarning')} ${formData.returnReason}`} />
                        )}
                        {formData.status === InvoiceStatus.REJECTED && formData.rejectionReason && (
                            <Message severity="error" className="w-full justify-content-start mb-3" text={`${th('dealerInvoice.rejectedWarning')} ${formData.rejectionReason}`} />
                        )}
                        {formData.status === InvoiceStatus.APPROVED && (
                            <Message severity="success" className="w-full justify-content-start mb-3" text={th('dealerInvoice.approvedInfo')} />
                        )}
                    </div>

                    {/* SAP Bilgi Kartı */}
                    <div className="col-12 mb-2">
                        <div className="surface-100 p-3 border-round border-left-3 border-primary-500">
                            <div className="flex justify-content-between align-items-center">
                                <div>
                                    <span className="text-gray-600 block text-sm font-semibold">{th('dealerInvoice.groupId')}</span>
                                    <span className="text-xl font-bold text-900">{formData.groupId}</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-gray-600 block text-sm font-semibold">{th('dealerInvoice.totalPrice')}</span>
                                    <span className="text-xl font-bold text-green-600">
                                        {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: formData.currency || 'TRY' }).format(formData.totalPrice || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Alanları - Grid Layout */}
                    <div className="col-12 md:col-6 field">
                        <label htmlFor="invoiceNumber" className="font-bold">
                            {th('dealerInvoice.invoiceNumber')}
                            {!isReadOnly && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <InputText
                            id="invoiceNumber"
                            value={formData.invoiceNumber || ''}
                            onChange={(e) => onInputChange(e, 'invoiceNumber')}
                            disabled={isReadOnly}
                            className={submitted && !formData.invoiceNumber ? 'p-invalid' : ''}
                            placeholder={th('dealerInvoice.invoiceNumberPlaceholder')}
                        />
                        {submitted && !formData.invoiceNumber && <small className="p-error">{th('dealerInvoice.messages.validationRequired')}</small>}
                    </div>

                    <div className="col-12 md:col-6 field">
                        <label htmlFor="invoiceDate" className="font-bold">
                            {th('dealerInvoice.invoiceDate')}
                            {!isReadOnly && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <Calendar
                            id="invoiceDate"
                            value={formData.invoiceDate}
                            onChange={(e) => onDateChange(e, 'invoiceDate')}
                            showIcon
                            dateFormat="dd/mm/yy"
                            disabled={isReadOnly}
                            className={submitted && !formData.invoiceDate ? 'p-invalid' : ''}
                        />
                        {submitted && !formData.invoiceDate && <small className="p-error">{th('dealerInvoice.messages.validationRequired')}</small>}
                    </div>

                    <div className="col-12 field">
                        <label htmlFor="description" className="font-bold">
                            {th('dealerInvoice.description')}
                            {!isReadOnly && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <InputTextarea
                            id="description"
                            value={formData.description || ''}
                            onChange={(e) => onTextareaChange(e, 'description')}
                            rows={3}
                            disabled={isReadOnly}
                            className={submitted && !formData.description?.trim() ? 'p-invalid' : ''}
                            placeholder={th('dealerInvoice.descriptionPlaceholder')}
                        />
                        {submitted && !formData.description?.trim() && <small className="p-error">{th('dealerInvoice.messages.validationRequired')}</small>}
                    </div>

                    {/* Dosya Yükleme Alanı */}
                    <div className="col-12 mt-2">
                        <label className="font-bold mb-2 block">
                            {th('dealerInvoice.documents')}
                            {!isReadOnly && <span className="text-red-500 ml-1">*</span>}
                        </label>
                        <GenericFileUpload
                            newFiles={newFiles}
                            onFilesChange={onFilesChange}
                            existingDocuments={existingDocuments}
                            onDeleteExistingDocument={onDeleteDocument}
                            onDownloadDocument={onDownloadDocument}
                            multiple={true}
                            maxFileSize={10000000}
                            showDeleteButton={!isReadOnly}
                            showDownloadButton={true}
                            disabled={isReadOnly || isSubmitting}
                            // Kabul edilen dosya tipleri
                            accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
                        />
                        {sendAttempted && hasNoDocuments && (
                            <small className="p-error block mt-1">
                                {th('dealerInvoice.messages.fileRequired')}
                            </small>
                        )}
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default DealerInvoiceDialog;
